import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { CenterArtistsPanel } from "./CenterArtistsPanel";
import { CenterUploadArtworkPanel } from "./CenterUploadArtworkPanel";
import { CenterMediaPanel } from "./CenterMediaPanel";
import { CenterPostsPanel } from "./CenterPostsPanel";
import { ProfileEditor } from "./ProfileEditor";

const CENTER_TABS = [
  { key: "overview", label: "Overview" },
  { key: "artists", label: "Artists" },
  { key: "upload", label: "Upload Artwork" },
  { key: "media", label: "Media & Docs" },
  { key: "stories", label: "Stories" },
  { key: "profile", label: "Profile" },
];

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="border border-[#E5E0D8] bg-white p-6">
    <p className="label-eyebrow text-[#555]">{label}</p>
    <p className="font-serif-display text-4xl mt-2 text-[#1E3A8A]">{value}</p>
  </div>
);

export const CenterDashboard = ({ user, onUserUpdated }) => {
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/center/overview");
      setData(d);
    } catch (_e) {
      toast.error("Could not load hub data");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    await act(() => api.post("/center/invite", { email: inviteEmail.trim() }), "Invitation sent");
    setInviteEmail("");
  };

  if (!data) return <p className="mt-12 label-eyebrow text-[#555]">Loading hub data…</p>;

  const { stats, members, pending, artworks, sales } = data;
  const requests = pending.filter((m) => m.initiated_by === "artist");
  const invitesSent = pending.filter((m) => m.initiated_by === "center");

  return (
    <div data-testid="center-dashboard" className="mt-12">
      <div className="flex items-center gap-2 border-b border-[#E5E0D8] overflow-x-auto" data-testid="center-tabs">
        {CENTER_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`center-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`label-eyebrow px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key
                ? "border-[#1E3A8A] text-[#1E3A8A]"
                : "border-transparent text-[#555] hover:text-[#111]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "artists" && (
        <div className="mt-8">
          <CenterArtistsPanel
            members={members}
            createFn={(payload) => api.post("/center/artists", payload)}
            onCreated={load}
          />
        </div>
      )}
      {tab === "upload" && (
        <div className="mt-8">
          <CenterUploadArtworkPanel members={members} onUploaded={load} />
        </div>
      )}
      {tab === "media" && (
        <div className="mt-8">
          <CenterMediaPanel user={user} />
        </div>
      )}
      {tab === "stories" && (
        <div className="mt-8">
          <CenterPostsPanel />
        </div>
      )}
      {tab === "profile" && (
        <ProfileEditor user={user} onUpdated={onUserUpdated} />
      )}

      {tab === "overview" && (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Member artists" value={stats.member_count} testid="center-stat-members" />
        <StatCard label="Works under hub" value={stats.artwork_count} testid="center-stat-artworks" />
        <StatCard label="Sales" value={stats.sales_count} testid="center-stat-sales" />
        <StatCard label="Sales total" value={`$${Number(stats.sales_total).toLocaleString()}`} testid="center-stat-total" />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to={`/art-hub/${user.id}`}
          data-testid="view-public-center-page"
          className="label-eyebrow border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-2.5"
        >
          View public hub page →
        </Link>
      </div>

      {/* Invite artists */}
      <div className="mt-12 border border-[#E5E0D8] bg-white p-6">
        <p className="label-eyebrow text-[#1E3A8A]">— Invite an artist</p>
        <p className="text-sm text-[#555] mt-2">
          Invite an existing AfricaArtMasters artist by their account email. They must accept before joining.
        </p>
        <form onSubmit={handleInvite} className="mt-4 flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="center-invite-email"
            type="email"
            required
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="artist@example.com"
            className="rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111] sm:max-w-sm"
          />
          <Button
            type="submit"
            data-testid="center-invite-submit"
            disabled={busy}
            className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-2 h-auto label-eyebrow"
          >
            Send invitation
          </Button>
        </form>
        {invitesSent.length > 0 && (
          <div className="mt-4 space-y-2">
            {invitesSent.map((m) => (
              <p key={m.id} data-testid={`sent-invite-${m.id}`} className="text-sm text-[#555]">
                Invitation to <span className="text-[#1E3A8A]">{m.artist_name}</span> — awaiting their response.
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Join requests */}
      <div className="mt-12">
        <p className="label-eyebrow text-[#555]">— Join requests</p>
        <h2 className="font-serif-display text-3xl mt-2 font-light">
          {requests.length} pending request{requests.length === 1 ? "" : "s"}
        </h2>
        {requests.length > 0 && (
          <div className="mt-6 space-y-3">
            {requests.map((m) => (
              <div
                key={m.id}
                data-testid={`join-request-${m.id}`}
                className="flex flex-wrap items-center justify-between gap-4 border border-[#E5E0D8] bg-white px-5 py-4"
              >
                <div>
                  <p className="font-serif-display text-xl">{m.artist_name}</p>
                  <p className="text-xs text-[#555] font-mono-aam mt-1">
                    Requested {new Date(m.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`approve-request-${m.id}`}
                    disabled={busy}
                    onClick={() => act(() => api.put(`/center/requests/${m.id}`, { action: "approve" }), `${m.artist_name} is now a member`)}
                    className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-5 py-2 h-auto label-eyebrow"
                  >
                    Approve
                  </Button>
                  <Button
                    data-testid={`reject-request-${m.id}`}
                    variant="outline"
                    disabled={busy}
                    onClick={() => act(() => api.put(`/center/requests/${m.id}`, { action: "reject" }), "Request rejected")}
                    className="rounded-none border-[#555] text-[#555] px-5 py-2 h-auto label-eyebrow"
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Members */}
      <div className="mt-12">
        <p className="label-eyebrow text-[#555]">— Member artists</p>
        <h2 className="font-serif-display text-3xl mt-2 font-light">{members.length} member{members.length === 1 ? "" : "s"}</h2>
        {members.length > 0 ? (
          <div className="mt-6 border border-[#E5E0D8] bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4EFE6]/40 border-b border-[#E5E0D8]">
                <tr>
                  <th className="text-left px-5 py-3 label-eyebrow text-[#555]">Artist</th>
                  <th className="text-left px-5 py-3 label-eyebrow text-[#555]">Country</th>
                  <th className="text-left px-5 py-3 label-eyebrow text-[#555]">Works under hub</th>
                  <th className="text-right px-5 py-3 label-eyebrow text-[#555]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} data-testid={`center-member-${m.artist_id}`} className="border-b border-[#E5E0D8] last:border-0">
                    <td className="px-5 py-3">
                      <Link to={`/artist/${m.artist_id}`} className="text-[#1E3A8A] hover:underline font-serif-display text-lg">
                        {m.artist_name}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-[#555]">{m.artist?.country || "—"}</td>
                    <td className="px-5 py-3 font-mono-aam">{m.artwork_count}</td>
                    <td className="px-5 py-3 text-right">
                      <Button
                        data-testid={`remove-member-${m.artist_id}`}
                        variant="outline"
                        disabled={busy}
                        onClick={() => {
                          if (!window.confirm(`Remove ${m.artist_name} from the hub? Their works will be detached.`)) return;
                          act(() => api.delete(`/center/members/${m.artist_id}`), "Member removed");
                        }}
                        className="rounded-none border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white px-4 py-1.5 h-auto label-eyebrow"
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-4 text-sm text-[#555]">
            No members yet — invite artists above, or share your public page so artists can request to join.
          </p>
        )}
      </div>

      {/* Artworks under hub */}
      <div className="mt-12">
        <p className="label-eyebrow text-[#555]">— Works presented by your hub</p>
        <h2 className="font-serif-display text-3xl mt-2 font-light">{artworks.length} work{artworks.length === 1 ? "" : "s"}</h2>
        {artworks.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {artworks.map((a) => (
              <div key={a.id} data-testid={`center-artwork-${a.id}`} className="border border-[#E5E0D8] bg-white p-4 flex gap-4">
                <img
                  src={resolveImg(a.image_url)}
                  alt={a.title}
                  className="w-20 h-20 object-cover border border-[#E5E0D8] flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <Link to={`/artwork/${a.id}`} className="font-serif-display text-lg leading-tight text-[#111] hover:text-[#1E3A8A] block truncate">
                    {a.title}
                  </Link>
                  <p className="text-xs text-[#555] mt-1 truncate">{a.artist_name} · ${Number(a.price).toLocaleString()}</p>
                  <p className="text-xs mt-1">
                    {a.sold ? (
                      <span className="text-[#D32F2F] label-eyebrow">Sold</span>
                    ) : (
                      <span className="label-eyebrow text-[#555]">{a.status === "draft" ? "Draft" : "Published"}</span>
                    )}
                  </p>
                  <button
                    type="button"
                    data-testid={`detach-artwork-${a.id}`}
                    disabled={busy}
                    onClick={() => act(() => api.put(`/center/artworks/${a.id}/detach`), "Work detached from hub")}
                    className="mt-2 label-eyebrow text-xs text-[#D32F2F] hover:underline"
                  >
                    Detach from hub
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sales */}
      <div className="mt-12">
        <p className="label-eyebrow text-[#555]">— Sales through your hub</p>
        <h2 className="font-serif-display text-3xl mt-2 font-light">
          {sales.length} sale{sales.length === 1 ? "" : "s"} · ${Number(stats.sales_total).toLocaleString()}
        </h2>
        {sales.length > 0 && (
          <div className="mt-6 border border-[#E5E0D8] bg-white">
            {sales.map((s) => (
              <div key={s.id || s.session_id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-[#E5E0D8] last:border-0">
                <div>
                  <p className="font-serif-display text-lg">{s.artwork_title || "Artwork"}</p>
                  <p className="text-xs text-[#555]">{s.artist_name} · {new Date(s.created_at).toLocaleDateString()}</p>
                </div>
                <p className="font-mono-aam text-[#1E3A8A]">${Number(s.amount || 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};
